/**
 * Role-based bill discount caps (F8 flow).
 *
 * Originally hard-coded inside BillDiscountModal; extracted to this module
 * so the normalization + platform-role detection can be unit-tested and
 * reused from the BillingPage's UI gating.
 *
 * TODO(FR-BILL-023): Once the tenant discount-rules sprint lands, replace
 *   the hard-coded `DEFAULT_CAPS` with a loader that reads from tenant
 *   settings (`/settings/billing/discount-caps`). The shape here is
 *   designed so that injection point lives in `getCapForRole` — callers
 *   don't need to change.
 *
 * See also:
 *   - docs/sdlc/phase5/PAGES.md §2.2 ("Discounts are capped based on role
 *     permission level")
 *   - docs/sdlc/phase1/REQUIREMENTS.md FR-BILL-023, FR-BILL-029, FR-BILL-050
 *   - CLAUDE.md §Role hierarchy (SUPER_ADMIN=100 > ADMIN=80 > MODERATOR=60 >
 *     OWNER=50 > MANAGER=30 > CASHIER=5)
 */

/**
 * Caps are expressed in the same units as `BillDiscount.value`:
 *   - `percent` is 0..100
 *   - `flat` is paise
 *
 * A `null` flat means "no flat cap", as the API serves it (OWNER, or a role
 * whose tenant removed the flat limit).
 */
export interface DiscountCap {
  percent: number;
  flat: number | null;
}

/**
 * Non-zero sentinel indicating the role reached the POS but is a *platform*
 * role (SUPER_ADMIN / ADMIN / MODERATOR). These roles should never be
 * writing bills — we surface a dedicated "platform role — use a tenant
 * account" message in the modal instead of the generic zero-cap warning.
 */
export type RolePermission =
  | { kind: 'allowed'; cap: DiscountCap }
  | { kind: 'platform' }
  | { kind: 'denied' };

// ─── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_CAPS: Record<string, DiscountCap> = {
  CASHIER: { percent: 10, flat: 20_000 }, // 10% or 20,000 minor units (200.00 in a 2-decimal currency)
  MANAGER: { percent: 50, flat: 500_000 }, // 50% or 500,000 minor units (5,000.00 in a 2-decimal currency)
  OWNER: { percent: 100, flat: null },
};

const PLATFORM_ROLES = new Set(['SUPER_ADMIN', 'ADMIN', 'MODERATOR']);


// ─── Tenant configuration ────────────────────────────────────────────────────

/**
 * The signed-in user's configured cap, served on the auth profile.
 *
 * Why a module-level value rather than a hook: `getRolePermission` and
 * `getCapForRole` are called inside a `useMemo` during render, and every
 * existing caller and test depends on them being synchronous. The auth store
 * sets this at login and before it publishes a reloaded profile, so the
 * helpers stay pure lookups.
 *
 * `null` means "not served" — an older API, or a profile fetched before this
 * shipped — and the DEFAULT_CAPS below apply, which is exactly what tills
 * enforced before the config was honoured.
 *
 * This drives what the cashier is SHOWN. The server re-checks every discount
 * against the tenant config; nothing here decides what is allowed.
 */
let configuredCap: { role: string; cap: DiscountCap } | null = null;

export function setConfiguredDiscountCap(role: string | null | undefined, cap: DiscountCap | null): void {
  configuredCap = cap && role ? { role: normalizeRole(role), cap } : null;
}

export function getConfiguredDiscountCap(): { role: string; cap: DiscountCap } | null {
  return configuredCap;
}

// ─── Public helpers ──────────────────────────────────────────────────────────

/**
 * Normalize any string role identifier to the canonical upper-case form the
 * role tables use. Returns empty string for null/undefined/empty input so
 * downstream lookups cleanly fall through to "denied".
 */
export function normalizeRole(role: string | null | undefined): string {
  return (role ?? '').trim().toUpperCase();
}

/**
 * Resolve a role to its discount permission.
 *
 * - Tenant staff with a configured cap → `allowed`
 * - Platform staff (SUPER_ADMIN/ADMIN/MODERATOR) → `platform` (special-cased
 *   so the UI can explain *why* they can't do this)
 * - Anyone else (unknown role, missing profile, lowercase mismatch that
 *   somehow survived normalization) → `denied`
 */
export function getRolePermission(role: string | null | undefined): RolePermission {
  const normalized = normalizeRole(role);
  if (!normalized) return { kind: 'denied' };
  if (PLATFORM_ROLES.has(normalized)) return { kind: 'platform' };
  const fallback = DEFAULT_CAPS[normalized];
  if (!fallback) return { kind: 'denied' };
  // The served cap belongs to the signed-in user, so it applies only to their
  // own role. Asking about a different role — the modal does this nowhere today,
  // but nothing stops it — still gets the shipped default rather than someone
  // else's limit.
  const cap = configuredCap && configuredCap.role === normalized ? configuredCap.cap : fallback;
  return { kind: 'allowed', cap };
}

/**
 * Back-compat convenience used by the modal's header/footer labels. Always
 * returns a `DiscountCap` — callers that need to branch on platform vs
 * unknown should use `getRolePermission` instead.
 */
export function getCapForRole(role: string | null | undefined): DiscountCap {
  const perm = getRolePermission(role);
  return perm.kind === 'allowed' ? perm.cap : { percent: 0, flat: 0 };
}
